/**
 * Page service for Shopify operations
 * Handles all page-related API calls with comprehensive validation
 */

import { BaseShopifyService } from "../services/base";
import { GET_PAGE_BY_HANDLE } from "./queries";
import { IOptionsRequest, IResponse } from "../../../models";
import { formatResponse } from "../../../core/utils";

export class PageService extends BaseShopifyService {
  /**
   * Get page by handle with enhanced parameter support
   * Supports custom query and variables through options
   */
  async getPageByHandle(
    params: { handle: string },
    options?: IOptionsRequest
  ): Promise<IResponse> {
    try {
      // Use custom query from options if provided, otherwise use default
      const query = options?.query || GET_PAGE_BY_HANDLE;

      // Merge params with custom variables from options
      const variables = {
        handle: params.handle,
        ...options?.variables, // Allow custom variables to override defaults
      };

      const { result, errors } = await this.query(query, variables, options);

      // ✅ Handle GraphQL or Shopify userErrors
      if (errors && errors.length > 0) {
        this.logger.error("GraphQL errors in GET_PAGE_BY_HANDLE", undefined, {
          errors,
        });

        return formatResponse({
          success: false,
          message: "Failed to fetch page due to upstream errors.",
          error: {
            message: "GraphQL errors received.",
            details: errors,
          },
        });
      }

      // ✅ Handle missing / empty data
      if (!result?.data || !result.data.page) {
        return formatResponse({
          success: true,
          message: "Request completed successfully. Page not found.",
          data: null,
          meta: undefined,
        });
      }

      // ✅ Normal success path
      return formatResponse({
        success: true,
        message: "Page retrieved successfully.",
        data: result.data.page,
        meta: undefined,
      });
    } catch (error: any) {
      // ✅ Log the error safely
      this.logger.error("Error in getPageByHandle", undefined, { error });

      const safeMessage =
        error instanceof Error
          ? error.message
          : "An unexpected error occurred while processing the request.";

      return formatResponse({
        success: false,
        message: "An error occurred while processing the request.",
        error: {
          message: safeMessage,
          details: error,
        },
      });
    }
  }
}
